import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, GripVertical, Pencil, Trash2, Plus, Check, X, RotateCcw, RefreshCw, ChevronDown, FileText, Rocket, Eye, Link } from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ThemeToggle } from '../../_shared/components';
import { ConditionBuilder } from './WaterfallConfig';
import { conditionSummary, conditionGroupSummary, CONDITION_FIELDS } from './waterfallTypes';
import {
  useEnrichmentFlags,
  useCreateEnrichmentFlag,
  useUpdateEnrichmentFlag,
  useDeleteEnrichmentFlag,
  useReorderEnrichmentFlags,
} from '../hooks/useEnrichmentFlags';
import { useLaunchedCampaigns, useResetCampaignStatus, useWipeDispatchLog, useRelaunchCampaign } from '../hooks/useLaunchedCampaigns';
import { useWebhookSettings, useUpdateWebhookSettings } from '../hooks/useWebhookSettings';
import { useCampaignTypeConfigs, useSyncStatus, useToggleCampaignType, useTriggerCampaignSync } from '../hooks/useCampaignTypeConfig';
import { useLauncherTemplates, useDeleteTemplate, useRenameTemplate } from '../hooks/useLauncherTemplates';
import type { WaterfallRow } from './waterfallTypes';
import type { EnrichmentFlag, ScorecardTemplate } from './types';
import { WaterfallRowsSchema } from '../wire-schemas';
import type { Condition } from './waterfallTypes';
import type { CampaignTypeConfig } from '../api-client';

// ── Inline edit form ──────────────────────────────────────────────────────────

interface EditFormProps {
  initial: Partial<EnrichmentFlag>;
  existingFlags: EnrichmentFlag[];
  onSave: (data: Omit<EnrichmentFlag, 'id'>) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function EditForm({ initial, existingFlags, onSave, onCancel, isSaving }: EditFormProps) {
  const [name, setName] = useState(initial.name ?? '');
  const [label, setLabel] = useState(initial.label ?? '');
  const [type, setType] = useState<'warning' | 'exclusion'>(initial.type ?? 'warning');
  const [conditions, setConditions] = useState<Condition[]>(
    initial.condition ? [initial.condition] : [],
  );
  const [conditionError, setConditionError] = useState<string | null>(null);

  const inputCls = 'px-2.5 py-1.5 text-xs bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-lg text-nightshade-900 dark:text-nightshade-50 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 focus:border-transparent transition';

  const handleConditionsChange = (next: Condition[]) => {
    setConditions(next);
    setConditionError(null);
  };

  const handleSave = () => {
    if (!name.trim()) return;
    const raw: Condition = conditions[0] ?? { field: 'suppressed', operator: 'equals', value: 'true' };
    // Normalize boolean condition values to canonical form so we always store 'true'/'false'
    const isBooleanField = CONDITION_FIELDS.find(f => f.field === raw.field)?.type === 'boolean';
    const condition: Condition = isBooleanField
      ? { ...raw, value: raw.operator === 'equals' ? 'true' : 'false' }
      : raw;
    const duplicate = existingFlags.find(f =>
      f.condition.field === condition.field &&
      f.condition.operator === condition.operator &&
      f.condition.value === condition.value,
    );
    if (duplicate) {
      setConditionError(`A flag with these same conditions already exists ("${duplicate.name}")`);
      return;
    }
    onSave({ name: name.trim(), label: label.trim() || name.trim(), condition, type, order: initial.order ?? 0 });
  };

  return (
    <div className="mt-2 p-4 bg-nightshade-50 dark:bg-nightshade-800/50 rounded-xl border border-nightshade-200 dark:border-nightshade-700 space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500">Name</label>
          <input
            className={`${inputCls} w-40`}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Suppressed"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500">Label (chip text)</label>
          <input
            className={`${inputCls} w-40`}
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="Same as name"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500">Type</label>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setType('warning')}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${type === 'warning' ? 'bg-lemon-400 text-lemon-900 border-lemon-400' : 'bg-lemon-50 dark:bg-lemon-950/30 text-lemon-700 dark:text-lemon-400 border-lemon-200 dark:border-lemon-800 hover:bg-lemon-100'}`}
            >
              Warning
            </button>
            <button
              type="button"
              onClick={() => setType('exclusion')}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${type === 'exclusion' ? 'bg-pomegranate-500 text-white border-pomegranate-500' : 'bg-pomegranate-50 dark:bg-pomegranate-950/30 text-pomegranate-700 dark:text-pomegranate-400 border-pomegranate-200 dark:border-pomegranate-800 hover:bg-pomegranate-100'}`}
            >
              Exclusion
            </button>
          </div>
        </div>
      </div>

      <div>
        <label className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 block mb-2">Condition</label>
        <ConditionBuilder
          conditions={conditions}
          logic="AND"
          onChange={handleConditionsChange}
          onLogicChange={() => {}}
          members={[]}
        />
        {conditionError && (
          <p className="mt-1.5 text-xs text-pomegranate-600 dark:text-pomegranate-400">{conditionError}</p>
        )}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleSave}
          disabled={!name.trim() || isSaving}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full bg-blueberry-500 text-white hover:bg-blueberry-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Check className="w-3 h-3" />
          Save
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-nightshade-200 dark:border-nightshade-600 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-100 dark:hover:bg-nightshade-700 transition-colors"
        >
          <X className="w-3 h-3" />
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Sortable flag row ─────────────────────────────────────────────────────────

interface FlagRowProps {
  flag: EnrichmentFlag;
  existingFlags: EnrichmentFlag[];
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (data: Omit<EnrichmentFlag, 'id'>) => void;
  onDelete: () => void;
  isSaving: boolean;
}

function SortableFlagRow({ flag, existingFlags, isEditing, onEdit, onCancelEdit, onSaveEdit, onDelete, isSaving }: FlagRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: flag.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-white dark:bg-nightshade-900 rounded-xl border border-nightshade-200 dark:border-nightshade-700 overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          {...attributes}
          {...listeners}
          className="text-nightshade-300 dark:text-nightshade-600 hover:text-nightshade-500 cursor-grab active:cursor-grabbing flex-shrink-0"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-4 h-4" />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-nightshade-900 dark:text-nightshade-50">{flag.name}</span>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                flag.type === 'exclusion'
                  ? 'bg-pomegranate-100 text-pomegranate-700 dark:bg-pomegranate-950/40 dark:text-pomegranate-400'
                  : 'bg-lemon-100 text-lemon-700 dark:bg-lemon-950/40 dark:text-lemon-400'
              }`}
            >
              {flag.type === 'exclusion' ? 'Exclusion' : 'Warning'}
            </span>
          </div>
          <p className="text-xs text-nightshade-400 dark:text-nightshade-500 mt-0.5">
            {conditionSummary([flag.condition], 'AND')}
          </p>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-1.5 text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200 transition-colors rounded-full hover:bg-nightshade-100 dark:hover:bg-nightshade-700"
            title="Edit"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-nightshade-400 hover:text-pomegranate-500 transition-colors rounded-full hover:bg-nightshade-100 dark:hover:bg-nightshade-700"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {isEditing && (
        <div className="px-4 pb-4">
          <EditForm
            initial={flag}
            existingFlags={existingFlags}
            onSave={onSaveEdit}
            onCancel={onCancelEdit}
            isSaving={isSaving}
          />
        </div>
      )}
    </div>
  );
}

// ── Template manager row ──────────────────────────────────────────────────────

function TemplateManagerRow({
  t,
  onDelete,
  onRename,
  isDeleting,
  isRenaming,
}: {
  t: Pick<ScorecardTemplate, 'id' | 'name' | 'campaign_type' | 'created_at' | 'source' | 'campaign_name' | 'rows'>;
  onDelete: () => void;
  onRename: (name: string) => void;
  isDeleting: boolean;
  isRenaming: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(t.name);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleSave = () => {
    if (editName.trim() && editName.trim() !== t.name) onRename(editName.trim());
    setEditing(false);
  };

  // Display-only: a template whose rows the UI cannot read lists no rows (the Admin page inspects those).
  const rows = useMemo(() => {
    const parsed = WaterfallRowsSchema.safeParse(t.rows);
    return parsed.success ? parsed.data : [];
  }, [t.rows]);
  const isLaunch = t.source === 'launch';

  return (
    <div className="border-b border-nightshade-50 dark:border-nightshade-800 last:border-0">
      <div className="flex items-center gap-2 px-4 py-3">
        {rows.length > 0 && (
          <button
            onClick={() => setExpanded(e => !e)}
            className="flex-shrink-0 p-0.5 text-nightshade-300 hover:text-nightshade-600 dark:text-nightshade-600 dark:hover:text-nightshade-300 transition-colors rounded"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-150 ${expanded ? '' : '-rotate-90'}`} />
          </button>
        )}
        {rows.length === 0 && <div className="w-5 flex-shrink-0" />}

        <div className="flex-1 min-w-0">
          {editing ? (
            <input
              autoFocus
              value={editName}
              onChange={e => setEditName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') { setEditName(t.name); setEditing(false); } }}
              onBlur={handleSave}
              className="w-full px-2 py-0.5 text-sm bg-white dark:bg-nightshade-800 border border-blueberry-400 rounded focus:outline-none"
            />
          ) : (
            <p className="text-sm font-medium text-nightshade-900 dark:text-nightshade-50 truncate">
              {isLaunch ? (t.campaign_name ?? t.name) : t.name}
            </p>
          )}
          <p className="text-xs text-nightshade-400 dark:text-nightshade-500 truncate">
            {isLaunch ? t.name : (t.campaign_type ?? '')}
            {t.created_at ? ` · ${new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
            {rows.length > 0 && ` · ${rows.length} rows`}
          </p>
        </div>

        <div className="flex items-center gap-0.5 flex-shrink-0">
          {confirmDelete ? (
            <>
              <span className="text-xs text-nightshade-500 dark:text-nightshade-400 mr-1">Delete?</span>
              <button
                onClick={() => { onDelete(); setConfirmDelete(false); }}
                disabled={isDeleting}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-full bg-pomegranate-500 text-white hover:bg-pomegranate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-full border border-nightshade-200 dark:border-nightshade-600 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-100 dark:hover:bg-nightshade-700 transition-colors ml-1"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              {!isLaunch && !editing && (
                <button
                  onClick={() => { setEditing(true); setConfirmDelete(false); }}
                  disabled={isRenaming}
                  className="p-1.5 text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200 transition-colors rounded-full hover:bg-nightshade-100 dark:hover:bg-nightshade-700"
                  title="Rename"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
              {rows.length > 0 && !editing && (
                <button
                  onClick={() => setExpanded(e => !e)}
                  className="p-1.5 text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200 transition-colors rounded-full hover:bg-nightshade-100 dark:hover:bg-nightshade-700"
                  title={expanded ? 'Hide rows' : 'View rows'}
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={() => setConfirmDelete(true)}
                className="p-1.5 text-nightshade-400 hover:text-pomegranate-500 transition-colors rounded-full hover:bg-nightshade-100 dark:hover:bg-nightshade-700"
                title="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      {expanded && rows.length > 0 && (
        <div className="px-10 pb-3 space-y-1.5">
          {rows.map((row, i) => (
            <div key={row.id ?? i} className="bg-nightshade-50 dark:bg-nightshade-800/50 rounded-md px-3 py-2">
              <p className="text-xs font-medium text-nightshade-700 dark:text-nightshade-200">{row.name}</p>
              {((row.condition_groups ?? []).length > 0 || row.conditions.length > 0) && (
                <p className="text-xs text-nightshade-400 dark:text-nightshade-500 mt-0.5 truncate" title={conditionGroupSummary(row.condition_groups ?? [], row.group_logic ?? 'AND')}>
                  {conditionGroupSummary(row.condition_groups ?? [], row.group_logic ?? 'AND')}
                </p>
              )}
              {(row.condition_groups ?? []).length === 0 && row.conditions.length === 0 && (
                <p className="text-xs text-nightshade-300 dark:text-nightshade-600 mt-0.5 italic">Catch-all</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function LauncherSettingsPage() {
  const navigate = useNavigate();
  const { flags, isLoading, isError } = useEnrichmentFlags();
  const { campaigns: launchedCampaigns, isLoading: launchedLoading } = useLaunchedCampaigns();
  const resetStatus = useResetCampaignStatus();
  const wipeLog = useWipeDispatchLog();
  const relaunch = useRelaunchCampaign();
  const [wipingId, setWipingId] = useState<string | null>(null);
  const [relaunchingId, setRelaunchingId] = useState<string | null>(null);
  const createFlag = useCreateEnrichmentFlag();
  const updateFlag = useUpdateEnrichmentFlag();
  const deleteFlag = useDeleteEnrichmentFlag();
  const reorderFlags = useReorderEnrichmentFlags();

  const { configs, isLoading: typesLoading, isError: typesError } = useCampaignTypeConfigs();
  const { syncStatus } = useSyncStatus();
  const toggleType = useToggleCampaignType();
  const triggerSync = useTriggerCampaignSync();

  const { templates: allTemplates } = useLauncherTemplates();
  const deleteTemplateMutation = useDeleteTemplate();
  const renameTemplateMutation = useRenameTemplate();

  const { settings: webhookSettings, isLoading: webhooksLoading } = useWebhookSettings();
  const updateWebhooks = useUpdateWebhookSettings();
  const [launchUrl, setLaunchUrl] = useState('');
  const [reenrichUrl, setReenrichUrl] = useState('');
  const [addMemberUrl, setAddMemberUrl] = useState('');
  const [campaignViewUrl, setCampaignViewUrl] = useState('');
  const [webhooksInitialized, setWebhooksInitialized] = useState(false);
  const [webhookSaved, setWebhookSaved] = useState(false);

  // Sync local state from fetched settings (once)
  if (!webhooksLoading && !webhooksInitialized) {
    setLaunchUrl(webhookSettings.launch_webhook_url ?? '');
    setReenrichUrl(webhookSettings.reenrich_webhook_url ?? '');
    setAddMemberUrl(webhookSettings.add_member_webhook_url ?? '');
    setCampaignViewUrl(webhookSettings.campaign_view_webhook_url ?? '');
    setWebhooksInitialized(true);
  }

  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);

  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const toggleSection = (key: string) =>
    setOpenSections(s => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = flags.findIndex(f => f.id === active.id);
    const newIndex = flags.findIndex(f => f.id === over.id);
    const reordered = arrayMove(flags, oldIndex, newIndex);
    reorderFlags.mutate(reordered.map(f => f.id));
  };

  const handleCreate = (data: Omit<EnrichmentFlag, 'id'>) => {
    setAddingNew(false);
    createFlag.mutate({ ...data, order: flags.length });
  };

  const handleUpdate = (id: string, data: Omit<EnrichmentFlag, 'id'>) => {
    setEditingId(null);
    updateFlag.mutate({ id, ...data });
  };

  return (
      <main className="flex-1 overflow-auto bg-nightshade-50 dark:bg-nightshade-950">
        <div className="max-w-2xl mx-auto px-6 py-8">

          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/launcher')}
                className="flex items-center text-nightshade-400 dark:text-nightshade-500 hover:text-nightshade-700 dark:hover:text-nightshade-200 transition-colors"
                aria-label="Back to Win Tool"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <h1 className="text-xl font-semibold tracking-tight text-nightshade-950 dark:text-nightshade-50">
                Enrichment Flags
              </h1>
            </div>
            <ThemeToggle />
          </div>
          <p className="text-sm text-nightshade-400 dark:text-nightshade-500 mb-6">
            Global rules applied to all campaigns. Exclusion flags show in the launch sidebar. Warning flags highlight rows in yellow.
          </p>

          {isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2].map(i => (
                <div key={i} className="h-16 bg-nightshade-100 dark:bg-nightshade-900 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : isError ? (
            <p className="text-sm text-pomegranate-500 dark:text-pomegranate-400">
              Failed to load enrichment flags.
            </p>
          ) : (
            <div className="space-y-2">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={flags.map(f => f.id)} strategy={verticalListSortingStrategy}>
                  {flags.map(flag => (
                    <SortableFlagRow
                      key={flag.id}
                      flag={flag}
                      existingFlags={flags.filter(f => f.id !== flag.id)}
                      isEditing={editingId === flag.id}
                      onEdit={() => { setEditingId(flag.id); setAddingNew(false); }}
                      onCancelEdit={() => setEditingId(null)}
                      onSaveEdit={data => handleUpdate(flag.id, data)}
                      onDelete={() => deleteFlag.mutate(flag.id)}
                      isSaving={updateFlag.isPending}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              {addingNew && (
                <div className="bg-white dark:bg-nightshade-900 rounded-xl border border-nightshade-200 dark:border-nightshade-700 px-4 pb-4 pt-3">
                  <p className="text-sm font-medium text-nightshade-800 dark:text-nightshade-100 mb-2">New flag</p>
                  <EditForm
                    initial={{ order: flags.length }}
                    existingFlags={flags}
                    onSave={handleCreate}
                    onCancel={() => setAddingNew(false)}
                    isSaving={createFlag.isPending}
                  />
                </div>
              )}

              {!addingNew && (
                <button
                  onClick={() => { setAddingNew(true); setEditingId(null); }}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-blueberry-500 hover:text-blueberry-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add flag
                </button>
              )}
            </div>
          )}
          {/* Launched campaigns */}
          <div className="mt-10">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-semibold text-nightshade-900 dark:text-nightshade-50">Launched campaigns</h2>
            </div>
            <p className="text-sm text-nightshade-400 dark:text-nightshade-500 mb-4">
              Reset a campaign back to draft so it can be re-launched. Wipe dispatch log removes all log rows for that campaign.
            </p>
            {launchedLoading ? (
              <div className="space-y-2">
                {[0, 1].map(i => <div key={i} className="h-12 bg-nightshade-100 dark:bg-nightshade-900 rounded-lg animate-pulse" />)}
              </div>
            ) : launchedCampaigns.length === 0 ? (
              <p className="text-sm text-nightshade-400 dark:text-nightshade-500">No launched campaigns.</p>
            ) : (
              <div className="space-y-2">
                {launchedCampaigns.map(c => (
                  <div key={c.id} className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-nightshade-900 rounded-xl border border-nightshade-200 dark:border-nightshade-700">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-nightshade-900 dark:text-nightshade-50 truncate">{c.name}</p>
                      <p className="text-xs text-nightshade-400 dark:text-nightshade-500">
                        {c.type ?? ''}{c.updated_at ? ` · launched ${new Date(c.updated_at).toLocaleDateString()}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {wipingId === c.id ? (
                        <>
                          <span className="text-xs text-nightshade-500 dark:text-nightshade-400">Wipe all log rows?</span>
                          <button
                            onClick={() => { wipeLog.mutate(c.id); setWipingId(null); }}
                            disabled={wipeLog.isPending}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-full bg-pomegranate-500 text-white hover:bg-pomegranate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setWipingId(null)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-full border border-nightshade-200 dark:border-nightshade-600 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-100 dark:hover:bg-nightshade-700 transition-colors"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setWipingId(c.id)}
                          disabled={wipeLog.isPending}
                          title="Wipe dispatch log"
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-nightshade-200 dark:border-nightshade-600 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-100 dark:hover:bg-nightshade-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                          Wipe log
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setRelaunchingId(c.id);
                          relaunch.mutate(c.id, { onSettled: () => setRelaunchingId(null) });
                        }}
                        disabled={relaunch.isPending}
                        title="Re-post all records to webhook"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-nightshade-200 dark:border-nightshade-600 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-100 dark:hover:bg-nightshade-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <Rocket className="w-3 h-3" />
                        {relaunchingId === c.id ? 'Re-launching...' : 'Re-launch'}
                      </button>
                      <button
                        onClick={() => resetStatus.mutate(c.id)}
                        disabled={resetStatus.isPending}
                        title="Reset to draft"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-nightshade-200 dark:border-nightshade-600 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-100 dark:hover:bg-nightshade-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <RotateCcw className="w-3 h-3" />
                        Reset
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Templates accordion */}
          <div className="mt-10">
            <button
              onClick={() => toggleSection('templates')}
              className="w-full flex items-center justify-between group"
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-nightshade-400" />
                <h2 className="text-base font-semibold text-nightshade-900 dark:text-nightshade-50">Templates</h2>
                <span className="text-xs text-nightshade-400 dark:text-nightshade-500">
                  {allTemplates.filter(t => t.source !== 'launch').length} saved
                </span>
              </div>
              <ChevronDown className={`w-4 h-4 text-nightshade-400 transition-transform duration-150 ${openSections.has('templates') ? '' : '-rotate-90'}`} />
            </button>

            {openSections.has('templates') && (
              <div className="mt-3 space-y-4">
                {/* Saved templates */}
                {allTemplates.filter(t => t.source !== 'launch').length === 0 ? (
                  <p className="text-sm text-nightshade-400 dark:text-nightshade-500">No saved templates yet.</p>
                ) : (
                  <div className="bg-white dark:bg-nightshade-900 rounded-lg border border-nightshade-200 dark:border-nightshade-700 overflow-hidden">
                    {allTemplates.filter(t => t.source !== 'launch').map(t => (
                      <TemplateManagerRow
                        key={t.id}
                        t={t}
                        onDelete={() => deleteTemplateMutation.mutate(t.id)}
                        onRename={name => renameTemplateMutation.mutate({ id: t.id, name })}
                        isDeleting={deleteTemplateMutation.isPending}
                        isRenaming={renameTemplateMutation.isPending}
                      />
                    ))}
                  </div>
                )}

                {/* Launch snapshots */}
                {allTemplates.filter(t => t.source === 'launch').length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Rocket className="w-3.5 h-3.5 text-nightshade-400 flex-shrink-0" />
                      <p className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 flex-shrink-0">
                        Launch snapshots ({allTemplates.filter(t => t.source === 'launch').length})
                      </p>
                      <div className="h-px flex-1 bg-nightshade-200 dark:bg-nightshade-700" />
                    </div>
                    <div className="bg-white dark:bg-nightshade-900 rounded-lg border border-nightshade-200 dark:border-nightshade-700 overflow-hidden">
                      {allTemplates.filter(t => t.source === 'launch').map(t => (
                        <TemplateManagerRow
                          key={t.id}
                          t={t}
                          onDelete={() => deleteTemplateMutation.mutate(t.id)}
                          onRename={name => renameTemplateMutation.mutate({ id: t.id, name })}
                          isDeleting={deleteTemplateMutation.isPending}
                          isRenaming={renameTemplateMutation.isPending}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Webhooks accordion */}
          <div className="mt-10">
            <button
              onClick={() => toggleSection('webhooks')}
              className="w-full flex items-center justify-between group"
            >
              <div className="flex items-center gap-2">
                <Link className="w-4 h-4 text-nightshade-400" />
                <h2 className="text-base font-semibold text-nightshade-900 dark:text-nightshade-50">Webhooks</h2>
              </div>
              <ChevronDown className={`w-4 h-4 text-nightshade-400 transition-transform duration-150 ${openSections.has('webhooks') ? '' : '-rotate-90'}`} />
            </button>

            {openSections.has('webhooks') && (
              <div className="mt-3">
                <p className="text-sm text-nightshade-400 dark:text-nightshade-500 mb-4">
                  Override the Clay webhook URLs used for launch and re-enrich dispatches. Leave blank to use the default (env var).
                </p>

                {webhooksLoading ? (
                  <div className="space-y-2">
                    {[0, 1, 2].map(i => <div key={i} className="h-10 bg-nightshade-100 dark:bg-nightshade-900 rounded-lg animate-pulse" />)}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 block mb-1">Launch Webhook URL</label>
                      <input
                        value={launchUrl}
                        onChange={e => { setLaunchUrl(e.target.value); setWebhookSaved(false); }}
                        placeholder="https://api.clay.com/v3/sources/webhook/..."
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-lg text-nightshade-900 dark:text-nightshade-50 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 focus:border-transparent transition"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 block mb-1">Re-enrich Webhook URL</label>
                      <input
                        value={reenrichUrl}
                        onChange={e => { setReenrichUrl(e.target.value); setWebhookSaved(false); }}
                        placeholder="https://api.clay.com/v3/sources/webhook/..."
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-lg text-nightshade-900 dark:text-nightshade-50 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 focus:border-transparent transition"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 block mb-1">Add Member Webhook URL</label>
                      <input
                        value={addMemberUrl}
                        onChange={e => { setAddMemberUrl(e.target.value); setWebhookSaved(false); }}
                        placeholder="https://api.clay.com/v3/sources/webhook/..."
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-lg text-nightshade-900 dark:text-nightshade-50 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 focus:border-transparent transition"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 block mb-1">Campaign View Webhook URL</label>
                      <input
                        value={campaignViewUrl}
                        onChange={e => { setCampaignViewUrl(e.target.value); setWebhookSaved(false); }}
                        placeholder="https://api.clay.com/v3/sources/webhook/..."
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-lg text-nightshade-900 dark:text-nightshade-50 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 focus:border-transparent transition"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          updateWebhooks.mutate(
                            { launch_webhook_url: launchUrl || null, reenrich_webhook_url: reenrichUrl || null, add_member_webhook_url: addMemberUrl || null, campaign_view_webhook_url: campaignViewUrl || null },
                            { onSuccess: () => setWebhookSaved(true) },
                          );
                        }}
                        disabled={updateWebhooks.isPending}
                        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full bg-blueberry-500 text-white hover:bg-blueberry-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {updateWebhooks.isPending ? 'Saving...' : 'Save'}
                      </button>
                      {webhookSaved && (
                        <span className="text-sm text-matcha-600 dark:text-matcha-400 flex items-center gap-1">
                          <Check className="w-3.5 h-3.5" /> Saved
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Campaign types accordion */}
          <div className="mt-10">
            <button
              onClick={() => toggleSection('campaign-types')}
              className="w-full flex items-center justify-between group"
            >
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-nightshade-900 dark:text-nightshade-50">Salesforce Campaign Types</h2>
              </div>
              <div className="flex items-center gap-3">
                {syncStatus?.campaign_hourly?.completed_at && (
                  <span className="text-xs text-nightshade-400 dark:text-nightshade-500">
                    Last synced {formatRelativeTime(syncStatus.campaign_hourly.completed_at)}
                    {syncStatus.campaign_hourly.status === 'error' && (
                      <span className="ml-1 text-pomegranate-500"> · sync failed</span>
                    )}
                  </span>
                )}
                <button
                  onClick={e => { e.stopPropagation(); triggerSync.mutate(); }}
                  disabled={triggerSync.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-nightshade-200 dark:border-nightshade-600 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-100 dark:hover:bg-nightshade-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <RefreshCw className={`w-3 h-3 ${triggerSync.isPending ? 'animate-spin' : ''}`} />
                  {triggerSync.isPending ? 'Syncing…' : 'Sync now'}
                </button>
                <ChevronDown className={`w-4 h-4 text-nightshade-400 transition-transform duration-150 ${openSections.has('campaign-types') ? '' : '-rotate-90'}`} />
              </div>
            </button>

            {openSections.has('campaign-types') && (
              <div className="mt-3">
                <p className="text-sm text-nightshade-400 dark:text-nightshade-500 mb-4">
                  Toggle which Salesforce campaign types appear in the launcher. Populated automatically on first sync.
                </p>

                {typesLoading ? (
                  <div className="space-y-px">
                    {[0, 1, 2, 3].map(i => (
                      <div key={i} className="h-10 bg-nightshade-100 dark:bg-nightshade-900 rounded-lg animate-pulse mb-1" />
                    ))}
                  </div>
                ) : typesError ? (
                  <p className="text-sm text-pomegranate-500 dark:text-pomegranate-400">Failed to load campaign types.</p>
                ) : configs.length === 0 ? (
                  <div className="flex items-center gap-3 px-4 py-4 bg-white dark:bg-nightshade-900 rounded-lg border border-nightshade-200 dark:border-nightshade-700">
                    <p className="text-sm text-nightshade-400 dark:text-nightshade-500">
                      No campaign types synced yet — click <span className="font-medium text-nightshade-600 dark:text-nightshade-300">Sync now</span> to pull from Salesforce.
                    </p>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-nightshade-900 rounded-lg border border-nightshade-200 dark:border-nightshade-700 overflow-hidden">
                    {groupByType(configs).map(({ type, rows }, groupIdx) => (
                      <div key={type}>
                        {groupIdx > 0 && <div className="border-t border-nightshade-100 dark:border-nightshade-800" />}
                        <div className="px-4 pt-3 pb-1 flex items-center gap-2">
                          <p className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 flex-shrink-0">{type}</p>
                          <div className="h-px flex-1 bg-nightshade-200 dark:bg-nightshade-700" />
                        </div>
                        {rows.map((cfg, rowIdx) => (
                          <div
                            key={`${cfg.type}-${cfg.subtype}`}
                            className={`flex items-center justify-between px-4 py-2.5 ${rowIdx < rows.length - 1 ? 'border-b border-nightshade-50 dark:border-nightshade-800' : ''}`}
                          >
                            <span className="text-sm text-nightshade-700 dark:text-nightshade-200">
                              {cfg.subtype ?? <span className="italic text-nightshade-400">No subtype</span>}
                            </span>
                            <button
                              role="switch"
                              aria-checked={cfg.enabled}
                              onClick={() => toggleType.mutate({ type: cfg.type, subtype: cfg.subtype, enabled: !cfg.enabled })}
                              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blueberry-400 focus:ring-offset-1 ${cfg.enabled ? 'bg-blueberry-500' : 'bg-nightshade-200 dark:bg-nightshade-700'}`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${cfg.enabled ? 'translate-x-4' : 'translate-x-0'}`}
                              />
                            </button>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                <p className="mt-2 text-xs text-nightshade-400 dark:text-nightshade-500">
                  Toggling hides or shows campaigns in the launcher. Does not affect launched campaigns or dispatch history.
                </p>
              </div>
            )}
          </div>

        </div>
      </main>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function groupByType(configs: CampaignTypeConfig[]): Array<{ type: string; rows: CampaignTypeConfig[] }> {
  const map = new Map<string, CampaignTypeConfig[]>();
  for (const c of configs) {
    if (!map.has(c.type)) map.set(c.type, []);
    map.get(c.type)!.push(c);
  }
  return Array.from(map.entries()).map(([type, rows]) => ({ type, rows }));
}

function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
